/**
 * Unsaved-changes guard for the settings screens (FRIDAY-43).
 *
 * Two guards run together:
 *
 *   - `beforeunload` for the browser-level case: a hard reload, closing the
 *     tab, or navigating to an external origin. That is the browser's own
 *     "Leave site?" prompt and is the only prompt that works without
 *     owning the navigation.
 *
 *   - TanStack Router `useBlocker` for the in-app case: a <Link> click
 *     inside the SPA does not fire `beforeunload`, so the edit would be
 *     lost silently. The blocker intercepts the route change, surfaces a
 *     confirm dialog, and only lets the navigation through when the user
 *     explicitly chooses to discard.
 *
 * `NavigationGuardProvider` is the screen-level owner of the blocker. It
 * aggregates "dirty" reports from each sub-section via context, so the
 * single confirm dialog sits at the route boundary even when the screen is
 * split into a board-identity section and a board-columns section. The hook
 * below is what each sub-section calls to register itself.
 *
 * The hook degrades gracefully when no router is in scope (e.g. unit tests
 * that mount a sub-section directly without RouterProvider): the beforeunload
 * listener still runs, the blocker simply never registers.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'
import { useBlocker, useRouter } from '@tanstack/react-router'
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog'

interface GuardApi {
  /** Register this id as dirty (active=true) or clean (active=false). */
  reportDirty: (id: string, active: boolean) => void
}

const GuardCtx = createContext<GuardApi | null>(null)

/**
 * Wrap any settings screen with this so the in-app blocker and the single
 * confirm dialog are owned by the screen itself (route-local wiring, not a
 * global shell). Sub-sections call `useUnsavedChangesGuard` to register.
 */
export function NavigationGuardProvider({ children }: { children: ReactNode }) {
  // `warn: false` so legacy mounts that render the screen outside a router
  // do not spam the console; the `disabled` flag below keeps the blocker
  // itself inert in that case.
  const router = useRouter({ warn: false })
  // A ref, not state: the set is only read inside `shouldBlockFn` when a
  // navigation is attempted, so a section changing its dirty flag does not
  // need to re-render the screen.
  const dirtySet = useRef(new Set<string>())

  const reportDirty = useCallback((id: string, active: boolean) => {
    if (active) dirtySet.current.add(id)
    else dirtySet.current.delete(id)
  }, [])
  // Stable context value so sections do not re-run their registration
  // effect (and briefly unregister) on every provider render.
  const api = useMemo<GuardApi>(() => ({ reportDirty }), [reportDirty])

  // Read the live set at navigation time rather than a value captured at
  // render: the blocker must never act on a stale "dirty" (or "clean") flag.
  const shouldBlockFn = useCallback(() => dirtySet.current.size > 0, [])
  const blocker = useBlocker({
    // Hold navigation while ANY registered section is dirty. The route-local
    // boundary is exactly where the unsaved edit can be lost, so this is the
    // only place the prompt belongs.
    shouldBlockFn,
    withResolver: true,
    // beforeunload is registered separately by `useUnsavedChangesGuard` so
    // unit mounts without a router still get tab-close protection.
    enableBeforeUnload: false,
    disabled: !router,
  })

  return (
    <GuardCtx.Provider value={api}>
      {children}
      <ConfirmDialog
        open={blocker.status === 'blocked'}
        title="Discard unsaved changes?"
        body="Leaving this screen will discard your unsaved changes. Stay on this page to keep editing."
        confirmLabel="Leave anyway"
        cancelLabel="Stay"
        // "Stay" cancels the navigation; "Leave anyway" proceeds and the
        // dirty React state evaporates with the unmount.
        onClose={() => blocker.reset?.()}
        onConfirm={() => blocker.proceed?.()}
      />
    </GuardCtx.Provider>
  )
}

/**
 * Register a section's dirty state with the screen's
 * `NavigationGuardProvider`. Also installs the browser-level
 * `beforeunload` listener so a tab close / hard reload is captured.
 *
 * Pass `active=false` when the section is clean or its save is in flight
 * — the latter lets the user navigate away once a save has been issued.
 */
export function useUnsavedChangesGuard(active: boolean): void {
  const api = useContext(GuardCtx)
  const id = useId()

  useEffect(() => {
    if (!api) return
    api.reportDirty(id, active)
    return () => api.reportDirty(id, false)
  }, [active, id, api])

  useEffect(() => {
    if (!active) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      // Legacy browsers need returnValue set; modern ones only look at
      // preventDefault. Setting both is the portable form.
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [active])
}
